import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { BrowserRouter as Router, Routes, Route, NavLink } from 'react-router-dom';
import * as THREE from 'three'
import { Canvas, useFrame } from '@react-three/fiber'
import { Image, Environment, ScrollControls, useScroll, useTexture, Sparkles, MeshTransmissionMaterial, useGLTF, useFBO, RoundedBox, Trail } from '@react-three/drei'
import { easing } from 'maath'
import { Text, Html, Billboard } from '@react-three/drei';
import Projects from './components/Projects';
import Experience from './components/Experience';
import Contact from './components/Contact';
import Medias from './components/Medias';
import './util.js'
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';



function App() {

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
    const [activeMobileCard, setActiveMobileCard] = useState(null);
    // CRA base URL (respects `homepage` in package.json)
    const ASSET_BASE = process.env.PUBLIC_URL || '';

    // Mobile swipe control for the carousel (touch drag left/right)
    const isDraggingRef = useRef(false);
    const lastXRef = useRef(0);
    const swipeRotationRef = useRef(0); // radians (accumulated)

    const onCanvasPointerDown = (e) => {
        if (!isMobile) return;
        if (e.pointerType !== 'touch') return;
        e.preventDefault();
        e.stopPropagation();
        isDraggingRef.current = true;
        lastXRef.current = e.clientX;
    };

    const onCanvasPointerMove = (e) => {
        if (!isMobile) return;
        if (!isDraggingRef.current) return;
        if (e.pointerType !== 'touch') return;
        e.preventDefault();
        e.stopPropagation();

        const dx = e.clientX - lastXRef.current;
        lastXRef.current = e.clientX;

        // Tune sensitivity here (higher = faster rotation)
        swipeRotationRef.current += dx * 0.01;
    };

    const endCanvasDrag = (e) => {
        if (!isMobile) return;
        if (e?.pointerType && e.pointerType !== 'touch') return;
        isDraggingRef.current = false;
    };

    useEffect(() => {
        // Close the menu if the user goes back to desktop width, and update isMobile state
        const onResize = () => {
            const mobile = window.innerWidth <= 900;
            setIsMobile(mobile);
            if (!mobile) setIsMobileMenuOpen(false);
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const closeMobileMenu = () => setIsMobileMenuOpen(false);

    useEffect(() => {
        const headerLogo = document.querySelector(".header_logo");

        if (!headerLogo) return;

        const handleMouseEnter = () => {
            let spans = headerLogo.querySelectorAll(".logo div span:not(:first-child)");

            spans.forEach((span, index) => {
                setTimeout(() => {
                    span.style.display = "inline-block";
                    setTimeout(() => {
                        span.style.opacity = "1";
                        span.style.transform = "translateX(0)";
                    }, 10);
                }, index * 100);
            });
        };

        const handleMouseLeave = () => {
            let spans = headerLogo.querySelectorAll(".logo div span:not(:first-child)");

            spans.forEach((span) => {
                span.style.opacity = "0";
                span.style.transform = "translateX(-20px)";
                setTimeout(() => {
                    span.style.display = "none";
                }, 400);
            });
        };

        headerLogo.addEventListener("mouseenter", handleMouseEnter);
        headerLogo.addEventListener("mouseleave", handleMouseLeave);

        return () => {
            headerLogo.removeEventListener("mouseenter", handleMouseEnter);
            headerLogo.removeEventListener("mouseleave", handleMouseLeave);
        };
    }, []);

    function CustomMouse({ ...props }) {
        const ref = useRef()
        const v = useRef(new THREE.Vector3())
        const dir = useRef(new THREE.Vector3())
        const pos = useRef(new THREE.Vector3())
        const [hovered, setHovered] = useState(false)
        const { nodes } = useGLTF(`${ASSET_BASE}/assets/models/lens-transformed.glb`)
        // NOTE: Avoid React state updates in useFrame (causes rerenders + stutter)
        // Using drei <Trail> instead of pushing positions into React state.

        useEffect(() => {
            return () => {
            document.body.style.cursor = 'auto'
            }
        }, [])

        useFrame(({ mouse, camera, delta }) => {
            if (!ref.current) return

            const vector = v.current.set(mouse.x, mouse.y, 0.5)
            vector.unproject(camera)

            dir.current.copy(vector).sub(camera.position).normalize()
            const distance = (8 - camera.position.z) / dir.current.z
            pos.current.copy(camera.position).add(dir.current.multiplyScalar(distance))

            easing.damp3(ref.current.position, pos.current, 0.15, delta)

            const targetScale = hovered ? [0.15, 0.15, 0.15] : [0.1, 0.1, 0.1]
            easing.damp3(ref.current.scale, targetScale, 0.1, delta)
        })

        return (
            <>
            {/* Lentille + trail (GPU/three-side, avoids React rerenders) */}
            <Trail
                width={0.15}
                length={6}
                decay={2}
                local={false}
                stride={0}
                interval={1}
            >
                <mesh
                ref={ref}
                scale={[0.2, 0.2, 0.2]}
                onPointerOver={() => setHovered(true)}
                onPointerOut={() => setHovered(false)}
                geometry={nodes.Cylinder.geometry}
                rotation-x={Math.PI / 2}
                {...props}
                >
                <MeshTransmissionMaterial
                    ior={1.4}
                    thickness={1}
                    anisotropy={0.2}
                    transmission={1}
                    envMapIntensity={2}
                    distortion={0.3}
                    distortionScale={0.2}
                    chromaticAberration={0.08}
                    temporalDistortion={0.3}
                />
                </mesh>
            </Trail>
            </>
        )
    }


    
    function Rig({ isMobile = false, swipeRotationRef = null, ...props }) {
        const ref = useRef()
        const scroll = useScroll()

        useFrame((state, delta) => {
            const targetY = isMobile && swipeRotationRef
                ? swipeRotationRef.current
                : -scroll.offset * (Math.PI * 2)

            // Smooth rotation
            easing.damp(ref.current.rotation, 'y', targetY, 0.25, delta)

            // state.events.update()   // Removed for performance
            easing.damp3(state.camera.position, [-state.pointer.x * 2, state.pointer.y, 16], 0.3, delta)
            state.camera.lookAt(0, 0, 0)
        })

        return <group ref={ref} {...props} />
    }
  
    function Carousel({ radius = 5, count = 10, isMobile = false, activeIndex = null, setActiveIndex = () => {} }) {
        // Tester ça a la place du cursor : https://codepen.io/soju22/pen/qEbdVjK
        return carouselCards.map((card, i) => {
            const angle = (i / count) * Math.PI * 2;
            const position = [
                Math.sin(angle) * radius,
                0,
                Math.cos(angle) * radius,
            ];

            return (
                <Card
                    key={i}
                    index={i}
                    url={card.url}
                    title={card.title}
                    description={card.description}
                    date={card.date}
                    position={position}
                    rotation={[0, Math.PI + angle, 0]}
                    isMobile={isMobile}
                    activeIndex={activeIndex}
                    setActiveIndex={setActiveIndex}
                />
            );
        });
    }

    function Card({
        index,
        url,
        title = "Titre",
        description = "Description",
        date,
        isMobile = false,
        activeIndex = null,
        setActiveIndex = () => {},
        ...props
    }) {
        const ref = useRef();
        const ref_img = useRef();

        const [hovered, hover] = useState(false);
        const showInfo = hovered;

        const pointerOver = (e) => {
            if (isMobile) return;
            e.stopPropagation();
            hover(true);
        };

        const pointerOut = () => {
            if (isMobile) return;
            hover(false);
        };

        const pointerDown = (e) => {
            if (!isMobile) return;
            e.stopPropagation();
            setActiveIndex((current) => (current === index ? null : index));
        };

        useFrame((state, delta) => {
            // Animations
            easing.damp3(ref.current.scale, hovered ? [1.2, 1.2, 1] : [1, 1, 1], 0.1, delta); 
            // easing.damp3(ref.current.scale, hovered ? 1.15 : 1, 0.1, delta);

            easing.damp(ref_img.current.material, 'radius', hovered ? 0.01 : 0.03, 0.2, delta);
            // easing.damp(ref_img.current.material, 'zoom', hovered ? 1 : 1.1, 0.2, delta);
            easing.damp(ref.current.rotation, 'z', hovered ? 0 : 0, 0.1, delta);
            easing.damp(ref.current.rotation, 'x', hovered ? 0 : 0, 0.1, delta);
        });

        return (
            <group ref={ref} {...props}>
                {/* Carte */}
                <Image
                    url={url}
                    transparent
                    side={THREE.DoubleSide}
                    // side={THREE.BackSide}
                    onPointerOver={pointerOver}
                    onPointerOut={pointerOut}
                    onPointerDown={pointerDown}
                    ref={ref_img}
                    onUpdate={(self) => {
                        if (self.material && self.material.map) {
                            // Appliquer une rotation de la texture
                            self.material.map.center.set(0.5, 0.5); // Centre de la texture pour pivoter correctement
                            self.material.map.rotation = Math.PI;   // Rotation de 180°
                            self.material.map.flipY = true;        // Désactive le flipY par défaut
                            
                            self.material.map.needsUpdate = true;   // Indique que la texture a changé
                        }
                    }}
                >
                    <bentPlaneGeometry args={[0.2, 2.9, 1.6, 20, 20]} />
                </Image>

                {/* Texte (desktop: hover 3D text, mobile: centered overlay under the image) */}
                {showInfo && !isMobile && (
                    <group position={[-1, -0.7, 0]} rotation={[0, Math.PI, 0]} className="glassmorphism_bg">
                        <Text
                            fontSize={0.2}
                            color="#ffffff"
                            anchorX="right"
                            anchorY="middle"
                            position={[1, -0.05, 1.5]}
                        >
                            {title}
                        </Text>

                        <Text
                            fontSize={0.1}
                            color="#ffffff"
                            anchorX="right"
                            anchorY="middle"
                            position={[1, -0.25, 1.5]}
                        >
                            {description}
                        </Text>

                        <Text
                            fontSize={0.1}
                            color="#ffffff"
                            anchorX="right"
                            anchorY="middle"
                            position={[1, -0.35, 1.5]}
                        >
                            {date}
                        </Text>
                    </group>
                )}

            </group>
        );
    }
    
    function Banner(props) {
        const ref = useRef()
        const texture = useTexture(`${ASSET_BASE}/assets/images/carousel/work_.png`)
        texture.wrapS = texture.wrapT = THREE.RepeatWrapping
        const scroll = useScroll()
        useFrame((state, delta) => {
            ref.current.material.time.value += Math.abs(scroll.delta) * 4
            ref.current.material.map.offset.x += delta / 2
        })
        return (
            <mesh ref={ref} {...props}>
                <cylinderGeometry args={[1.6, 1.6, 0.14, 128, 16, true]} />
                <meshSineMaterial map={texture} map-anisotropy={16} map-repeat={[30, 1]} side={THREE.DoubleSide} toneMapped={false} />
            </mesh>
        )
    }
  

    // Carousel cards data for homepage
    const carouselCards = [
        { url: `${ASSET_BASE}/assets/images/carousel/aerobay_home.webp`, title: 'Aerobay', description: 'Thymeleaf▫JS▫Three▫SEO', date: '2026' },
        { url: `${ASSET_BASE}/assets/images/carousel/catacombes.webp`, title: 'Catacombes', description: 'JS▫Three▫WebXR▫SEO▫AR▫VR', date: '2026' },
        { url: `${ASSET_BASE}/assets/images/carousel/leon_cyprien.webp`, title: 'Leon et Cyprien', description: 'React▫R3F▫Electron', date: '2025' },
        { url: `${ASSET_BASE}/assets/images/carousel/sophiemarechal_avocat.webp`, title: 'Sophie Marechal', description: 'React▫SEO', date: '2025' },
        { url: `${ASSET_BASE}/assets/images/carousel/dsn8.webp`, title: 'DS N°8', description: 'Three▫JS▫ARKit▫WebXR', date: '2024' },
        { url: `${ASSET_BASE}/assets/images/carousel/devxr_classic.webp`, title: 'DevXR', description: 'React▫R3F▫SEO', date: '2024' },
        { url: `${ASSET_BASE}/assets/images/carousel/devxr_immersif.webp`, title: 'DevXR', description: 'React▫R3F▫SEO', date: '2024' },
        { url: `${ASSET_BASE}/assets/images/carousel/cv.webp`, title: 'CV', description: 'JS▫Three', date: '2023' },
        { url: `${ASSET_BASE}/assets/images/carousel/portfolio_old.webp`, title: 'Portfolio', description: 'JS▫Three▫WebXR', date: '2022' },
        { url: `${ASSET_BASE}/assets/images/carousel/portfolio_home.webp`, title: 'Portfolio', description: 'JS▫Three', date: '2022' },
    ];

    function MobileCardOverlay({ card }) {
        if (!card) return null;
        // Portal to <body> so position:fixed is not affected by any transformed parents
        return createPortal(
            <div className="mobileCardInfo">
                <div className="mobileCardInfoInner">
                    <div className="mobileCardTitle">{card.title}</div>
                    <div className="mobileCardDesc">{card.description}</div>
                    <div className="mobileCardDate">{card.date}</div>
                </div>
            </div>,
            document.body
        );
    }

    return (
        <Router basename="/portfolio">
            <div className="app-container">
                

                <header className="header">

                    {/* <img src="" alt="Test logo" /> */}
                    <div className="header_logo">
                        <div className="logo">
                            <div>
                                <span>P</span>
                                <span>A</span>
                                <span>U</span>
                                <span>L</span>
                                <span>&nbsp;&nbsp;</span>
                            </div>
                            <div>
                                <span>M</span>
                                <span>A</span>
                                <span>R</span>
                                <span>E</span>
                                <span>C</span>
                                <span>H</span>
                                <span>A</span>
                                <span>L</span>
                            </div>
                        </div>
                    </div>

                    {/* Burger (mobile only) */}
                    <button
                        className={`burgerButton ${isMobileMenuOpen ? 'isOpen' : ''}`}
                        aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
                        aria-expanded={isMobileMenuOpen}
                        onClick={() => setIsMobileMenuOpen((v) => !v)}
                        type="button"
                    >
                        {
                            isMobileMenuOpen ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="burgerIcon">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                    <path d="M18 6l-12 12" />
                                    <path d="M6 6l12 12" />
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="burgerIcon">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                    <path d="M4 8l16 0" />
                                    <path d="M4 16l16 0" />
                                </svg>
                            )
                        }
                    </button>

                    {/* Backdrop (mobile only) */}
                    <div
                        className={`mobileMenuBackdrop ${isMobileMenuOpen ? 'isOpen' : ''}`}
                        onClick={closeMobileMenu}
                    />

                    <nav className={`nav-header ${isMobileMenuOpen ? 'isOpen' : ''}`}>
                        <NavLink to="/" className="nav-link" onClick={closeMobileMenu}> Home </NavLink>
                        <NavLink to="/projets" className="nav-link" onClick={closeMobileMenu}>Projets</NavLink>
                        <NavLink to="/experience" className="nav-link" onClick={closeMobileMenu}>Expérience</NavLink>
                        <NavLink to="/medias" className="nav-link" onClick={closeMobileMenu}>Médias</NavLink>
                        <NavLink to="/contact" className="nav-link" onClick={closeMobileMenu}>Contact</NavLink>
                    </nav>
                </header>

                <main className="main_content">
                    <Routes>
                        <Route
                            path="/projets"
                            element={
                                <div className="display_charts">
                                    <div className="user-chart-container">
                                        <Projects />
                                    </div>
                                </div>
                            }
                        />
                        <Route
                            path="/experience"
                            element={
                                <div className="display_users_form">
                                    <div className="user-list-container">
                                        <Experience />
                                    </div>
                                </div>
                            }
                        />
                        <Route
                            path="/medias"
                            element={
                                <div className="display_users_form">
                                    <div className="user-list-container">
                                        <Medias />
                                    </div>
                                </div>
                            }
                        />
                        <Route
                            path="/contact"
                            element={
                                <div className="display_users_form">
                                    <div className="user-list-scrollable">
                                        <Contact />
                                    </div>
                                </div>
                            }
                        />
                        <Route
                            path="*"
                            element={
                                <div className="homepage_display">
                                    
                                    <Canvas
                                        dpr={[1, 2]}
                                        camera={{ position: [0, 0, isMobile ? 130 : 100], fov: isMobile ? 30 : 16 }}
                                        style={{ width: '100%', height: '100%', touchAction: isMobile ? 'none' : 'pan-y' }}
                                        onPointerDown={onCanvasPointerDown}
                                        onPointerMove={onCanvasPointerMove}
                                        onPointerUp={endCanvasDrag}
                                        onPointerLeave={endCanvasDrag}
                                        onPointerCancel={endCanvasDrag}
                                        onPointerMissed={() => {
                                            if (isMobile) setActiveMobileCard(null);
                                        }}
                                    >
                                        {/* <fog attach="fog" args={['#6b6b6b', 5, 15]} /> */}
                                        <ScrollControls pages={5} damping={0.1} infinite>
                                            <Rig rotation={[0, 0, 0]} isMobile={isMobile} swipeRotationRef={swipeRotationRef}>
                                                <Carousel
                                                    isMobile={isMobile}
                                                    activeIndex={activeMobileCard}
                                                    setActiveIndex={setActiveMobileCard}
                                                />
                                            </Rig>
                                            {/* <Banner position={[0, -0.15, 0]} /> */}
                                        </ScrollControls>
                                        <Environment preset="dawn" blur={0.5} />
                                        <Sparkles count={500} scale={25} size={10} speed={0.4} />
                                        <CustomMouse />
                                        <ambientLight intensity={5} />
                                    </Canvas>
                                    {isMobile && activeMobileCard !== null && (
                                        <MobileCardOverlay card={carouselCards[activeMobileCard]} />
                                    )}
                                </div>
                            }
                        />
                    </Routes>
                </main>
            </div>
        </Router>
    );
}

export default App;
